import type { RuleSet } from '../../../rules';
import {
  isStarving,
  MILLI,
  type PlayerId,
  playerStock,
  type SimState,
  upkeepOf,
} from '../../../sim';
import { cityName, ownCities } from '../../cities';
import type { Names } from '../../names';
import { clockText } from '../../upgradeProgress';
import type { GameEvent } from '../types';

/**
 * The "food running out" condition: the viewer's net food rate measured from
 * a sliding window of stock samples, and the alert raised while the stock
 * would run dry soon (see `foodAlert`). It is a condition, not an event: the
 * feed keeps one entry under FOOD_KEY while it holds and drops it when it
 * stops (feed.ts, `addEvents`' `active`).
 */

/** The condition key of the food alert. */
export const FOOD_KEY = 'food';
/** Seconds of stock samples the net rate is measured over. */
export const FOOD_RATE_WINDOW = 10;
/** The rate needs at least this much of the window filled before it is trusted. */
const MIN_SPAN = FOOD_RATE_WINDOW / 2;
/** Seconds to empty under which the alert is raised. */
export const FOOD_WARN_SECONDS = 60;
/** Extra seconds to empty it must climb past FOOD_WARN_SECONDS to clear, so it doesn't flicker. */
export const FOOD_CLEAR_MARGIN = 15;
/** A raised alert survives a net rate this close to zero (per second): a flat window isn't relief. */
export const FOOD_FLAT_RATE = 0.05;
/**
 * A drop in food between two samples beyond what upkeep drains in that time
 * by more than this (units) is spending, not the sustained rate.
 */
export const FOOD_SPEND_SPIKE = 2;
/** Seconds a raised alert's relief (a rate above FOOD_FLAT_RATE) must hold before it clears. */
export const FOOD_RELIEF_SECONDS = FOOD_RATE_WINDOW;

/** The viewer's food (units, fractional) at a sim time, and the upkeep food/s then. */
export interface FoodSample {
  elapsed: number;
  food: number;
  upkeep: number;
}

/** What the food alert remembers from frame to frame. */
export interface FoodTracker {
  samples: FoodSample[];
  /** Whether the alert held last frame (its hysteresis, see `foodRunningOut`). */
  raised: boolean;
  /** Since when a raised alert's relief has held (null: not in relief). */
  reliefSince: number | null;
}

export function createFoodTracker(): FoodTracker {
  return { samples: [], raised: false, reliefSince: null };
}

/**
 * Adds a sample and drops those older than FOOD_RATE_WINDOW (in place). A
 * sample at the same time as the last replaces it; a clock that went back (a
 * new match) forgets the old samples.
 */
export function pushFoodSample(
  samples: FoodSample[],
  elapsed: number,
  food: number,
  upkeep = 0
): void {
  const last = samples[samples.length - 1];
  if (last && elapsed < last.elapsed) samples.length = 0;
  else if (last && elapsed === last.elapsed) samples.pop();
  samples.push({ elapsed, food, upkeep });
  const keep = samples.findIndex((s) => s.elapsed >= elapsed - FOOD_RATE_WINDOW);
  if (keep > 0) samples.splice(0, keep);
}

/**
 * The sustained net food per second over the samples (income less upkeep),
 * or null while they cover under half the window. A step that drops more
 * than the upkeep drains in its time plus FOOD_SPEND_SPIKE is spending (a
 * purchase, a queued unit) and left out, so one purchase doesn't read as a
 * falling income while a big army's drain still counts.
 */
export function foodRate(samples: readonly FoodSample[]): number | null {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last || last.elapsed - first.elapsed < MIN_SPAN) return null;
  let change = 0;
  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const d = samples[i].food - prev.food;
    const drain = prev.upkeep * (samples[i].elapsed - prev.elapsed);
    if (d >= -(FOOD_SPEND_SPIKE + drain)) change += d;
  }
  return change / (last.elapsed - first.elapsed);
}

/**
 * Whether food is running out: the net rate is negative and the stock lasts
 * under FOOD_WARN_SECONDS. Once `raised` it holds through the hysteresis:
 * until the stock lasts FOOD_CLEAR_MARGIN s longer, through a flat rate (within
 * FOOD_FLAT_RATE of zero) and while the rate is unknown (null, after a reset).
 */
export function foodRunningOut(rate: number | null, food: number, raised: boolean): boolean {
  if (rate === null) return raised;
  if (raised) {
    if (rate > FOOD_FLAT_RATE) return false;
    if (rate >= 0) return true;
    return food / -rate < FOOD_WARN_SECONDS + FOOD_CLEAR_MARGIN;
  }
  return rate < 0 && food / -rate < FOOD_WARN_SECONDS;
}

/**
 * The food alert for this frame, or null when it doesn't hold. Samples the
 * viewer's food into the tracker first. Once soldiers are losing hp
 * (`isStarving`) the starvation banner says so and the alert hands over to
 * it. With the stock out and upkeep unpaid (the grace period running,
 * `starvingSince`) it counts down that grace instead. A raised alert clears
 * only once relief has held for FOOD_RELIEF_SECONDS, so one deposit lump
 * entering the window doesn't clear it (and re-raise it fresh). With no known
 * net drain it counts down at the upkeep alone.
 */
export function foodAlert(
  tracker: FoodTracker,
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): GameEvent | null {
  const upkeep = upkeepOf(state, rules, playerId).food ?? 0;
  const food = (playerStock(state, playerId).food ?? 0) / MILLI;
  pushFoodSample(tracker.samples, state.elapsed, food, upkeep);
  if (isStarving(state, rules, playerId)) {
    tracker.raised = false;
    tracker.reliefSince = null;
    return null;
  }
  const rate = foodRate(tracker.samples);
  const since = state.players[playerId]?.starvingSince;
  const unpaid = food < 1 && since !== undefined;
  let running = unpaid || foodRunningOut(rate, food, tracker.raised);
  if (tracker.raised && !running) {
    tracker.reliefSince ??= state.elapsed;
    running = state.elapsed - tracker.reliefSince < FOOD_RELIEF_SECONDS;
  } else {
    tracker.reliefSince = null;
  }
  tracker.raised = running;
  if (!running) return null;
  const grace = rules.pacing.starvationGrace;
  const stock = Math.floor(food);
  const drain = rate !== null && rate < 0 ? -rate : upkeep;
  const figures = unpaid
    ? `Out of food, upkeep ${upkeep.toFixed(1)}/s not covered.`
    : `${drain > 0 ? `−${drain.toFixed(1)}/s` : 'Flat'} with ${stock} in stock.`;
  const detail = [
    figures,
    `Soldiers lose hp after ${grace} s with no food.`,
    !unpaid && upkeep > 0 ? `Upkeep ${upkeep.toFixed(1)}/s.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const left = unpaid ? grace - (state.elapsed - (since ?? 0)) : drain > 0 ? food / drain : 0;
  // The city holding the most food (the first on a tie): where to go to fix it.
  const cities = ownCities(state, rules, playerId);
  const city = cities.reduce<(typeof cities)[number] | undefined>(
    (best, c) => (!best || (c.stock?.food ?? 0) > (best.stock?.food ?? 0) ? c : best),
    undefined
  );
  return {
    kind: 'food',
    key: FOOD_KEY,
    text: 'Food running out',
    detail,
    // To empty, or once empty to attrition (as the top bar's "starving in" chip).
    badge: `${unpaid ? 'starving in' : 'food out in'} ${clockText(left)}`,
    ...(city
      ? {
          cityId: city.id,
          action: `Open ${cityName(city, playerId, cityNumbers, undefined, names)}`,
        }
      : {}),
  };
}
